import { useQuery } from "@apollo/client";
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Redirect } from "react-router-dom";
import {
  GetOrganizationSettingsQuery,
  GetOrganizationSettingsQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Integrations from "@apps/org-settings/components/integrations";
import IntegrationsScopeTabs from "@apps/org-settings/components/integrations/integrations-scope-tabs";
import Notifications from "@apps/org-settings/components/notifications";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Container from "@components/container/container";
import GraphqlError from "@components/error/graphql-error";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { role } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import Billing from "./components/billing";
import AllRolesCompetencies from "./components/competencies/all-roles-competencies";
import CareerTrackForm from "./components/competencies/career-track-form/career-track-form";
import CareerTrackOverview from "./components/competencies/career-track-overview";
import { careerTrackOverviewPath } from "./components/competencies/helpers";
import MemberCareerOverview from "./components/competencies/member-career-overview";
import PersonCareerView from "./components/competencies/person-career-view";
import CoreValues from "./components/core-values";
import DirectorySync from "./components/directory-sync";
import IntegrationRouteRedirect from "./components/integrations/integration-route-redirect";
import Members from "./components/members";
import Settings from "./components/settings";
import TalentIndicators from "./components/talent-indicators";
import Teams from "./components/teams";
import getOrganizationSettingsQuery from "./graphql/get-organization-settings-query";

const IT_ADMIN_ALLOWED_TABS = new Set([
  "directory-sync",
  "integrations",
  "notifications",
]);

const FULL_WIDTH_PATTERNS = ["/members", "/competencies", "/career-track"];

const isFullWidthPage = (pathname: string) =>
  FULL_WIDTH_PATTERNS.some((pattern) => pathname.includes(pattern));

// Pages that render their own SettingsPageHeader.
const PAGE_HEADER_PATTERNS = ["/competencies", "/career-track"];

const hasPageHeader = (pathname: string) =>
  PAGE_HEADER_PATTERNS.some((pattern) => pathname.includes(pattern));

// The department id lives in the URL, so the table reads it here.
const DepartmentCompetenciesRoute = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const { teamId } = useParams<{ teamId: string }>();
  return (
    <AllRolesCompetencies
      key={teamId}
      organizationId={organizationId}
      teamId={Number(teamId)}
    />
  );
};

const OrgSettings = () => {
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  useDocumentTitle(`${label("organization", { capitalize: true })} settings`);
  const location = useLocation<TFLocationState>();
  const { path } = useRouteMatch();
  const { organizationId: organizationIdParam } = useParams<{
    organizationId: string;
  }>();
  const organizationId =
    parseInt(organizationIdParam) || currentOrganization.id;

  // HOOKS
  const { data, loading } = useQuery<
    GetOrganizationSettingsQuery,
    GetOrganizationSettingsQueryVariables
  >(getOrganizationSettingsQuery);
  const organizations = data?.me?.organizations
    ? assertEdgesNonNull(data.me.organizations)
    : [];
  const selectedOrganization = organizations.find(
    ({ id }) => id === organizationId,
  );
  const membershipRole = selectedOrganization?.userMembership?.role ?? null;
  const hasOrganizationAccess = membershipRole !== null;
  const hasSettingsAccess =
    hasOrganizationAccess &&
    [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(membershipRole);

  // Render the integrations header in the parent so it remains visible across
  // route transitions (otherwise switching to /account/integrations unmounts
  // the page and shows only a loading spinner with no tabs). Must be called
  // before any early return to keep hook order stable.
  const isIntegrationsRoute = !!useRouteMatch({
    path: `${path}/integrations`,
    exact: true,
  });

  if (selectedOrganization && !hasOrganizationAccess) {
    return <Redirect to="/" />;
  }

  const firstAllowedTab = hasSettingsAccess
    ? membershipRole === role.itAdmin
      ? "directory-sync"
      : "settings"
    : "integrations";
  const defaultTabPath = selectedOrganization
    ? `/settings/organization/${selectedOrganization.id}/${firstAllowedTab}`
    : "/";
  const hasAccess = (tabValue: string) => {
    if (!membershipRole) {
      return false;
    }
    if (tabValue === "integrations") {
      return true;
    }
    if (membershipRole === role.itAdmin) {
      return IT_ADMIN_ALLOWED_TABS.has(tabValue);
    }
    return [role.globalAdmin, role.peopleAdmin].includes(membershipRole);
  };

  const integrationsHeader = isIntegrationsRoute && (
    <div className="pb-8">
      <h2 className="text-2xl font-medium text-gray-800 mb-2">Integrations</h2>
      <IntegrationsScopeTabs
        selectedScope="organization"
        organizationId={organizationId}
      />
    </div>
  );

  return (
    <Container
      ariaLabel="Organization Settings"
      maxWidth={isFullWidthPage(location.pathname) ? "full" : "lg"}
      padding="settings"
      hideFloatingHeader={hasPageHeader(location.pathname)}
      contentBackground={hasPageHeader(location.pathname)}
    >
      {integrationsHeader}
      {loading ? (
        <Loading />
      ) : selectedOrganization ? (
        <div className="text-gray-800">
          <Switch>
            <Route exact path={[`${path}/settings`, `${path}`]}>
              {hasAccess("settings") ? (
                <Settings organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/members`}>
              {hasAccess("members") ? (
                <Members organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/teams`}>
              {hasAccess("teams") ? (
                <Teams organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/core-values`}>
              {hasAccess("core-values") ? (
                <CoreValues organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/ai`}>
              <Redirect to="/agent" />
            </Route>
            <Route exact path={`${path}/workspace-skills`}>
              <Redirect to="/agent" />
            </Route>
            <Route exact path={`${path}/billing`}>
              {hasAccess("billing") ? (
                <Billing organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/directory-sync`}>
              {hasAccess("directory-sync") ? (
                <DirectorySync organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/integrations`}>
              {hasAccess("integrations") ? (
                <Integrations
                  organization={selectedOrganization}
                  canManage={hasSettingsAccess}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/integrations/:slug`}>
              {hasAccess("integrations") ? (
                <IntegrationRouteRedirect
                  organizationId={selectedOrganization.id}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/slack`}>
              {hasAccess("integrations") ? (
                <Redirect
                  to={`/settings/organization/${selectedOrganization.id}/integrations?open=slack`}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/notifications`}>
              {hasAccess("notifications") ? (
                <Notifications organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/talent-indicators`}>
              {hasAccess("talent-indicators") ? (
                <TalentIndicators organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {/* Career Tracks opens on the overview: every department and
                career track, and the way in to each. Competencies used to
                open on pages of their own; they open in a modal now, so old
                links land here too. Everyone in the organization has an
                overview: those who don't run the ladder see their own. */}
            <Route
              exact
              path={[
                `${path}/competencies`,
                // The old Competencies tab: its table is on the overview now.
                `${path}/competencies/list`,
                `${path}/competencies/new`,
                `${path}/competencies/edit/:competencyId`,
              ]}
            >
              <Redirect
                to={
                  hasOrganizationAccess
                    ? careerTrackOverviewPath(selectedOrganization.id)
                    : defaultTabPath
                }
              />
            </Route>
            {/* Tier one: every department and career track in one table. For
                everyone else, the tracks they and their reports are on. */}
            <Route exact path={`${path}/career-track/overview`}>
              {hasAccess("competencies") ? (
                <CareerTrackOverview organizationId={selectedOrganization.id} />
              ) : hasOrganizationAccess ? (
                <MemberCareerOverview
                  organizationId={selectedOrganization.id}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/career-track/all-roles`}>
              {hasAccess("competencies") ? (
                <AllRolesCompetencies
                  organizationId={selectedOrganization.id}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {/* The same table with every competency, whoever it reaches. */}
            <Route exact path={`${path}/career-track/all-competencies`}>
              {hasAccess("competencies") ? (
                <AllRolesCompetencies
                  organizationId={selectedOrganization.id}
                  showAllCompetencies
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {/* The same table, narrowed to one department's competencies. */}
            <Route exact path={`${path}/career-track/department/:teamId`}>
              {hasAccess("competencies") ? (
                <DepartmentCompetenciesRoute
                  organizationId={selectedOrganization.id}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {/* One person: their role's column, and criteria just for them.
                Everyone can open their own, and managers their reports' (to
                edit their own criteria); the page checks who it's showing. */}
            <Route exact path={`${path}/career-track/person/:userId`}>
              {hasOrganizationAccess ? (
                <PersonCareerView
                  organizationId={selectedOrganization.id}
                  canManageTracks={hasAccess("competencies")}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {/* One route for both: a new track swaps its URL for its own id
                as soon as it saves, and a single route keeps the editor
                mounted (and whatever is being typed) through that swap. */}
            <Route
              exact
              path={[
                `${path}/career-track/new`,
                `${path}/career-track/edit/:careerTrackId`,
              ]}
            >
              {hasAccess("competencies") ? (
                <CareerTrackForm organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
          </Switch>
        </div>
      ) : (
        <GraphqlError
          whatDidNotWork="Organization settings could not be loaded."
          error={new Error("Organization settings could not be loaded")}
        />
      )}
    </Container>
  );
};

export default OrgSettings;
